import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Briefcase,
  CheckCircle2,
  Compass,
  HandCoins,
  Lightbulb,
  MessageSquare,
  Network,
  ShieldCheck,
  ShoppingBasket,
  Users,
  Vote,
  Wheat,
  Wrench,
} from 'lucide-react';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../config/featureFlags';

const movementLayers = [
  {
    layer: 'Layer 1',
    title: 'Encounter Christ personally',
    body: 'The movement begins with a person meeting Jesus, learning to hear and obey Him, growing closer to God, and becoming rooted in prayer, Scripture, repentance, love, and faithful daily life.',
    icon: Wheat,
  },
  {
    layer: 'Layer 2',
    title: 'The local church becomes family',
    body: 'Like the early church in Acts, believers gather often: praying, worshipping, learning, sharing meals, breaking bread, carrying one another’s burdens, sharing what they have, and serving side by side.',
    icon: Users,
  },
  {
    layer: 'Layer 3',
    title: 'Neighboring churches become neighbors',
    body: 'Congregations and Christian communities in the same area meet one another, pray and eat together, share testimonies, invite one another into service, and build real fellowship without erasing their identities or traditions.',
    icon: Network,
  },
  {
    layer: 'Layer 4',
    title: 'Regions share strength',
    body: 'Local fellowships connect across counties, cities, and states so churches can respond to larger needs, exchange resources, support ministries, and coordinate gatherings and action beyond one neighborhood.',
    icon: Compass,
  },
  {
    layer: 'The horizon',
    title: 'One global Body of Christ',
    body: 'Believers across nations become able to recognize one another, share provision, discern needs, and move together as one Body—living toward God’s original design for humanity: communion with Him, love for one another, faithful stewardship, and life ordered toward His Kingdom.',
    icon: ShieldCheck,
  },
];

const toolCards = [
  {
    title: 'Kingdom Circles',
    body: 'Purpose-driven spaces for churches, ministries, neighborhoods, businesses, and mission teams to organize real work together.',
    icon: Network,
  },
  {
    title: 'Ideas',
    body: 'Members can put forward ideas, refine them together, discern what is wise, and make transparent decisions that lead to faithful action.',
    icon: Vote,
  },
  {
    title: 'Transparency',
    body: 'A community should be able to see what was proposed, what changed, how members participated, what passed, and what actually happened afterward.',
    icon: ShieldCheck,
  },
  {
    title: 'Marketplace',
    body: 'Goods can be bought, sold, or bartered with stronger trust signals, clearer trade history, and a path toward principled Christian commerce.',
    icon: ShoppingBasket,
  },
  {
    title: 'Jobs & Roles',
    body: 'Communities can define needed roles, receive applications, approve the right people, and make work visible instead of hidden in informal chats.',
    icon: Briefcase,
  },
  {
    title: 'Services',
    body: 'Members and Christian-principled businesses can offer practical services that meet real needs, from skilled labor to care and consultation.',
    icon: Wrench,
  },
  {
    title: 'Provision & stewardship',
    body: 'Funding, treasurer-issued receipts, material gifts, volunteer commitments, and transparent records help people understand where resources came from and where they went.',
    icon: HandCoins,
  },
  {
    title: 'Mission Globe',
    body: 'The globe points attention toward places where people are suffering, where the Gospel is needed, and where coordinated Kingdom action can begin.',
    icon: Compass,
  },
];

const ideaFlow = [
  {
    title: 'Pray',
    body: 'Start by asking what God is putting in front of the group, not merely what sounds impressive.',
  },
  {
    title: 'Ideate',
    body: 'Post the idea clearly enough that other members can understand the need, the people affected, and the possible path forward.',
  },
  {
    title: 'Pray again',
    body: 'Refinement should leave room for wisdom, correction, and a better version of the idea than one person could have written alone.',
  },
  {
    title: 'Vote',
    body: 'Members decide with visible results, so the group can move without pretending every voice has to disappear into a private back room.',
  },
  {
    title: 'Pray again',
    body: 'Before action, the group pauses long enough to remember that this is about obedience, love, courage, and service.',
  },
  {
    title: 'Act',
    body: 'Approved ideas become tasks, owners, funding needs, progress updates, and a record of what was done.',
  },
];

const conflictSteps = [
  'A concern is raised with enough context that people can understand what happened.',
  'The people involved are given room to respond instead of being judged by rumor.',
  'Resolvers organize the discussion around truth, repair, accountability, and peace.',
  'A resolution is proposed, tracked, accepted where appropriate, and connected back to action.',
];

export default function LearnPage() {
  const marketplaceEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceListings);
  const jobsEnabled = isFeatureEnabled(FEATURE_FLAGS.jobsPayroll);
  const servicesEnabled = isFeatureEnabled(FEATURE_FLAGS.services);
  const visibleToolCards = toolCards.filter(card => card.title !== 'Marketplace' || marketplaceEnabled)
    .filter(card => card.title !== 'Jobs & Roles' || jobsEnabled)
    .filter(card => card.title !== 'Services' || servicesEnabled);
  return (
    <main className="learn-page">
      <section className="learn-hero">
        <div className="learn-hero-copy">
          <span className="learn-kicker">
            <Wheat size={18} aria-hidden="true" />
            Bird&apos;s eye view
          </span>
          <h1>How it all works</h1>
          <p className="learn-hero-lede">
            TYK is a practical tool built to support the Kingdom Movement.
          </p>
          <p>
            The movement is the Body of Christ drawing closer to God and to one another—from personal encounter, to
            stronger local churches, to fellowship among neighboring congregations, and eventually to worldwide
            communion. TYK supports that movement by helping believers connect, pray, share needs and resources,
            discern ideas, coordinate service, and turn shared conviction into visible action.
          </p>
          <div className="learn-actions">
            <Link to="/post-group" className="learn-primary-action">
              Connect your church or start a Kingdom Circle <ArrowRight size={18} />
            </Link>
            <a href="/#home-globe-heading" className="learn-secondary-action">
              Open the Mission Globe
            </a>
          </div>
        </div>
        <aside className="learn-hero-panel" aria-label="TYK summary">
          <div className="learn-panel-stat">
            <strong>The Kingdom Movement</strong>
            <span>Relationships first. Tools that help the Body move together.</span>
          </div>
          <div className="learn-mini-flow">
            {['Pray', 'Ideate', 'Vote', 'Act'].map((step) => (
              <span key={step}>{step}</span>
            ))}
          </div>
        </aside>
      </section>

      <section className="learn-section" aria-labelledby="learn-overview-heading">
        <div className="learn-section-heading">
          <span className="learn-kicker">
            <BookOpen size={18} aria-hidden="true" />
            The Kingdom Movement
          </span>
          <h2 id="learn-overview-heading">From personal encounter to one global Body</h2>
          <p>
            TYK is meant to help relationships grow outward in healthy layers. Technology does not create the Body of
            Christ; it helps people find one another, stay connected, and coordinate the faithful life they are already called to live.
          </p>
        </div>
        <div className="learn-movement-grid">
          {movementLayers.map(({ layer, title, body, icon: Icon }, index) => (
            <article key={title} className="learn-movement-layer">
              <span className="learn-movement-number">{String(index + 1).padStart(2, '0')}</span>
              <div className="learn-card-icon">
                <Icon size={24} aria-hidden="true" />
              </div>
              <p className="learn-movement-kicker">{layer}</p>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="learn-section learn-flow-section" aria-labelledby="learn-flow-heading">
        <div className="learn-section-heading">
          <span className="learn-kicker">
            <Lightbulb size={18} aria-hidden="true" />
            From idea to action
          </span>
          <h2 id="learn-flow-heading">How a problem becomes coordinated work</h2>
          <p>
            The point is not endless debate. The point is a faithful rhythm that helps a community recognize a need,
            sharpen an idea, decide clearly, and then get to work.
          </p>
        </div>
        <ol className="learn-flow">
          {ideaFlow.map(({ title, body }, index) => (
            <li key={title}>
              <span className="learn-flow-number">{index + 1}</span>
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="learn-section" aria-labelledby="learn-tools-heading">
        <div className="learn-section-heading">
          <span className="learn-kicker">
            <CheckCircle2 size={18} aria-hidden="true" />
            How the tools fit together
          </span>
          <h2 id="learn-tools-heading">The app is not one feature. It is a working system.</h2>
          <p>
            Each surface has a job: help people gather, decide, build trust, exchange value, and follow through.
          </p>
        </div>
        <div className="learn-tool-grid">
          {visibleToolCards.map(({ title, body, icon: Icon }) => (
            <article key={title} className="learn-tool-card">
              <Icon size={22} aria-hidden="true" />
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="learn-section learn-conflict-section" aria-labelledby="learn-conflict-heading">
        <div className="learn-conflict-copy">
          <span className="learn-kicker">
            <MessageSquare size={18} aria-hidden="true" />
            Conflict resolution
          </span>
          <h2 id="learn-conflict-heading">Peace has to be organized too</h2>
          <p>
            Conflict does not disappear because a group has a good mission. TYK treats reconciliation as part of the
            work. When people disagree, the platform should help them slow down, tell the truth, protect the vulnerable,
            and pursue repair instead of letting resentment rot in the dark.
          </p>
        </div>
        <ol className="learn-conflict-steps">
          {conflictSteps.map((step, index) => (
            <li key={step}>
              <span>{index + 1}</span>
              <p>{step}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="learn-section learn-cta-section" aria-labelledby="learn-cta-heading">
        <div>
          <span className="learn-kicker">
            <Wheat size={18} aria-hidden="true" />
            Choose the next faithful step
          </span>
          <h2 id="learn-cta-heading">Start where God has already put your attention.</h2>
          <p>
            Bring a church into connection, start a Kingdom Circle, explore the Mission Globe, or begin with the
            marketplace and services people need right now.
          </p>
        </div>
        <div className="learn-cta-actions">
          <Link to="/post-group" className="learn-primary-action">Connect or start</Link>
          {marketplaceEnabled && <Link to="/marketplace" className="learn-secondary-action">Marketplace</Link>}
          {jobsEnabled && <Link to="/jobs" className="learn-secondary-action">Jobs</Link>}
          {servicesEnabled && <Link to="/services" className="learn-secondary-action">Services</Link>}
        </div>
      </section>
    </main>
  );
}
